import React, { FunctionComponent, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import moment from 'moment';
import { trim } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { useSwipeable } from 'react-swipeable';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { referenceCompanyValidationSchema } from '../../../../common/validation/yup';
import Input from '../../../../components/Input';
import RegularButton from '../../../../components/RegularButton';
import { ErrorMessage } from '../../../../components/ErrorMessage';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import { stringToDate } from '../../../../common/helpers/dateHelper';

export type ReferenceCompany = {
  id?: string;
  workInCompany: string | null;
  workStartDate: Date | null;
  workEndDate: Date | null;
}

export type ReferenceCompanyFormValues = {
  id?: string;
  workInCompany: string;
  workStartDate: string;
  workEndDate: string | null;
}

interface IProps {
  firstName: string;
  value: ReferenceCompany;
  onGoBack: IBindingAction;
  onSubmit: IBindingCallback1<ReferenceCompany>;
}

const ReferenceCompanyForm: FunctionComponent<IProps> = ({
  firstName,
  value,
  onGoBack,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: ReferenceCompanyFormValues = {
    id: value.id,
    workInCompany: value.workInCompany || '',
    workStartDate: value.workStartDate ? moment(value.workStartDate).format('MMyyyy') : '',
    workEndDate: value.workEndDate ? moment(value.workEndDate).format('MMyyyy') : ''
  };

  const getErrors = (errors: FormikErrors<ReferenceCompany>, values: ReferenceCompanyFormValues) => {
    const startDateError = errors.workStartDate as string;
    const endDateError = errors.workEndDate as string;
    return (
      <div className="mt-3">
        {(values.workStartDate && startDateError) && (
          <ErrorMessage id={startDateError} defaultMessage={startDateError} />
        )}
        {(values.workEndDate && endDateError) && <ErrorMessage id={endDateError} defaultMessage={endDateError} />}
      </div>
    );
  };

  const toggleIsSubmiting = () => {
    if (!isSubmitting) {
      setIsSubmitting(true);
    }
  };

  const submit = ({ workInCompany, workStartDate, workEndDate }: ReferenceCompanyFormValues) => {
    toggleIsSubmiting();
    onSubmit({
      id: initialValues.id,
      workInCompany: trim(workInCompany),
      workStartDate: stringToDate(workStartDate),
      workEndDate: workEndDate ? stringToDate(workEndDate) : null
    });
  };

  const { handleSubmit, values, setFieldValue, errors, isValid } = useFormik({
    initialValues,
    onSubmit: submit,
    validationSchema: referenceCompanyValidationSchema,
    validateOnMount: true,
    validateOnChange: true
  });

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="cf-container">
      <div className="cf-header-title mb-2">
        <FormattedMessage id="reference" defaultMessage="Reference" />
        &nbsp;
        {initialValues.id}
      </div>
      <div className="cf-title mb-2">
        <FormattedMessage
          id="candidateForm.company.title"
          defaultMessage="Tell us more about when you and {firstName} worked together"
          values={{ firstName }}
        />
      </div>
      <div className="cf-subtitle">
        <FormattedMessage
          id="candidateForm.company.subtitle"
          defaultMessage="Approximate start and end dates are fine."
        />
      </div>
      <form onSubmit={handleSubmit} autoComplete="off" className="w-100">
        <div className="cf-form">
          <Input
            autoFocus
            type="text"
            label={{ id: 'candidateForm.company', defaultMessage: 'Company name' }}
            value={values.workInCompany}
            error={isSubmitting && Boolean(errors.workInCompany && values.workInCompany)}
            onChange={v => setFieldValue('workInCompany', v)}
            variant="underlined"
            size="lg"
          />
          <div className="cf-inputs-container">
            <Input
              type="date"
              label={{ id: 'candidateForm.workStartDate', defaultMessage: 'Start date' }}
              value={values.workStartDate}
              error={isSubmitting && Boolean(errors.workStartDate && values.workStartDate)}
              success={!errors.workStartDate && Boolean(values.workStartDate)}
              onChange={val => setFieldValue('workStartDate', val)}
              variant="underlined"
              size="lg"
            />
            <Input
              type="date"
              label={{ id: 'candidateForm.lastDate', defaultMessage: 'End date' }}
              value={values.workEndDate || ''}
              error={isSubmitting && Boolean(errors.workEndDate && values.workEndDate)}
              success={!errors.workEndDate && Boolean(values.workEndDate)}
              onChange={val => setFieldValue('workEndDate', val)}
              variant="underlined"
              size="lg"
            />
          </div>
        </div>
        <div className="cf-footer-btns">
          <IconButton
            size="lg"
            icon={<Icon icon="arrowLeft" />}
            onClick={onGoBack}
          />
          <RegularButton
            disabled={!isValid}
            size="lg"
            textIntlId="candidateForm.next"
            defaultMessage="Next"
            icon={<Icon icon="arrowRight" />}
            iconPosition="right"
            onClick={() => {
              toggleIsSubmiting();
              handleSubmit();
            }}
            type="submit"
            enableAction
          />
        </div>
        {isSubmitting && getErrors(errors, values)}
      </form>
    </div>
  );
};

export default ReferenceCompanyForm;
